// Architecture explorer: <div data-arch="marketplace"></div> becomes a diagram
// drawn from scripts/arch-data.js. Selecting a component (click, or Tab then
// Enter) lights it and its connections and shows what it does; when the data
// has steps, a walkthrough below lights each step's components in turn.
// Plain SVG and DOM, no library. Works inside the case-study panel too.
import { ARCHITECTURES } from "./arch-data";

const SVG = "http://www.w3.org/2000/svg";
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]);

const KIND_NAMES = {
  http: "HTTPS call (synchronous)",
  kafka: "Kafka event (asynchronous)",
  external: "Third-party API",
  discovery: "Service lookup",
  db: "Own database / index",
};

function el(name, attrs = {}, parent) {
  const node = document.createElementNS(SVG, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, v);
  if (parent) parent.appendChild(node);
  return node;
}

// A straight edge between two aligned nodes, from border to border
function straightPath(a, b) {
  if (Math.abs(a.x - b.x) < 1) {
    const down = b.y > a.y;
    const y1 = a.y + (down ? a.h / 2 : -a.h / 2);
    const y2 = b.y + (down ? -b.h / 2 : b.h / 2);
    return `M${a.x} ${y1} V${y2}`;
  }
  const right = b.x > a.x;
  const x1 = a.x + (right ? a.w / 2 : -a.w / 2);
  const x2 = b.x + (right ? -b.w / 2 : b.w / 2);
  return `M${x1} ${a.y} H${x2}`;
}

function list(title, items) {
  if (!items || !items.length) return "";
  return `<dt>${esc(title)}</dt><dd><ul>${items.map((i) => `<li>${esc(i)}</li>`).join("")}</ul></dd>`;
}

function render(container, data) {
  const byId = Object.fromEntries(data.nodes.map((n) => [n.id, n]));
  const [vx, vy, vw, vh] = data.viewBox;
  const name = container.dataset.arch;

  container.classList.add("ax");
  container.innerHTML = `
    <div class="ax__stage">
      <div class="ax__scroll"></div>
    </div>
    <ul class="ax__legend" aria-label="Legend">
      ${["http", "kafka", "external", "discovery", "db"]
        .filter((k) => data.edges.some((e) => e.kind === k))
        .map((k) => `<li><svg width="34" height="10" aria-hidden="true"><line class="ax-edge ax-edge--${k}" x1="1" y1="5" x2="33" y2="5" /></svg>${esc(KIND_NAMES[k])}</li>`)
        .join("")}
    </ul>
    <div class="ax__picker" role="group" aria-label="Components"></div>
    <div class="ax__panel" aria-live="polite"></div>`;
  const scroll = container.querySelector(".ax__scroll");
  const panel = container.querySelector(".ax__panel");
  const picker = container.querySelector(".ax__picker");

  const svg = el("svg", { viewBox: `${vx} ${vy} ${vw} ${vh}`, class: "ax__svg", role: "group", "aria-label": `${data.title}: select a component to see its role and connections` });
  scroll.appendChild(svg);
  const defs = el("defs", {}, svg);
  for (const kind of ["http", "kafka", "external", "discovery"]) {
    const m = el("marker", { id: `ax-${name}-${kind}`, viewBox: "0 0 8 8", refX: "7", refY: "4", markerWidth: "7", markerHeight: "7", orient: "auto-start-reverse", class: `ax-arrow ax-arrow--${kind}` }, defs);
    el("path", { d: "M0 0.6 L7.4 4 L0 7.4 Z" }, m);
  }

  // Edges first, so the nodes cover their ends
  const edgeLayer = el("g", { class: "ax__edges" }, svg);
  const labelLayer = el("g", { class: "ax__labels", "aria-hidden": "true" }, svg);
  const edgeEls = {};
  for (const e of data.edges) {
    const d = e.path || straightPath(byId[e.from], byId[e.to]);
    const path = el("path", { d, class: `ax-edge ax-edge--${e.kind}`, "data-edge": e.id }, edgeLayer);
    if (e.kind !== "db") {
      path.setAttribute("marker-end", `url(#ax-${name}-${e.kind})`);
      if (e.both) path.setAttribute("marker-start", `url(#ax-${name}-${e.kind})`);
    }
    edgeEls[e.id] = { path, edge: e };
  }

  const nodeLayer = el("g", { class: "ax__nodes" }, svg);
  const nodeEls = {};
  for (const n of data.nodes) {
    const g = el("g", { class: `ax-node ax-node--${n.kind}`, "data-node": n.id, tabindex: "0", role: "button", "aria-pressed": "false", "aria-label": `${n.label}${n.sub ? ", " + n.sub : ""}` }, nodeLayer);
    el("rect", { x: n.x - n.w / 2, y: n.y - n.h / 2, width: n.w, height: n.h, rx: n.kind === "data" || n.kind === "infra" ? n.h / 2 : 8 }, g);
    const t = el("text", { x: n.x, y: n.sub ? n.y - 3 : n.y + 1, class: "ax-node__label" }, g);
    t.textContent = n.label;
    if (n.sub) {
      const s = el("text", { x: n.x, y: n.y + 11, class: "ax-node__sub" }, g);
      s.textContent = n.sub;
    }
    nodeEls[n.id] = g;
    g.addEventListener("click", () => select({ type: "node", id: n.id }));
    g.addEventListener("keydown", (ev) => {
      if (ev.key === "Enter" || ev.key === " ") {
        ev.preventDefault();
        select({ type: "node", id: n.id });
      }
    });
  }

  // Edge labels at the middle of their path, now that it is in the page
  for (const { path, edge } of Object.values(edgeEls)) {
    if (!edge.label) continue;
    const p = edge.lx ? { x: edge.lx, y: edge.ly + 5 } : path.getPointAtLength(path.getTotalLength() / 2);
    const t = el("text", { x: p.x, y: p.y - 5, class: "ax-edge__label", "text-anchor": edge.anchor || "middle" }, labelLayer);
    t.textContent = edge.label;
  }

  // The same components as buttons: easier to reach on a phone
  for (const n of data.nodes.filter((n) => n.label !== "MongoDB")) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "ax__chip";
    b.dataset.node = n.id;
    b.textContent = n.label;
    b.setAttribute("aria-pressed", "false");
    b.addEventListener("click", () => select({ type: "node", id: n.id }));
    picker.appendChild(b);
  }

  // On a narrow screen the drawing scrolls sideways: start at its centre,
  // where the entry point is
  requestAnimationFrame(() => {
    scroll.scrollLeft = (scroll.scrollWidth - scroll.clientWidth) / 2;
  });

  // ---- walkthrough ----
  let steps = null;
  if (data.steps) {
    steps = document.createElement("section");
    steps.className = "ax-steps";
    steps.setAttribute("aria-label", "Purchase walkthrough");
    steps.innerHTML = `
      <div class="ax-steps__head">
        <h3 class="ax-steps__title">Follow a purchase</h3>
        <div class="ax-steps__nav">
          <button type="button" class="ax-steps__btn" data-dir="-1" aria-label="Previous step"><i class="fa fa-arrow-left" aria-hidden="true"></i></button>
          <span class="ax-steps__count" aria-live="polite"></span>
          <button type="button" class="ax-steps__btn" data-dir="1" aria-label="Next step"><i class="fa fa-arrow-right" aria-hidden="true"></i></button>
        </div>
      </div>
      <ol class="ax-steps__list">
        ${data.steps.map((s, i) => `<li><button type="button" class="ax-steps__item" data-step="${i}" aria-pressed="false"><span>${i + 1}</span>${esc(s.title)}</button></li>`).join("")}
      </ol>
      <div class="ax-steps__card"></div>
`;
    container.appendChild(steps);
    steps.querySelectorAll("[data-step]").forEach((b) => b.addEventListener("click", () => select({ type: "step", i: Number(b.dataset.step) })));
    steps.querySelectorAll("[data-dir]").forEach((b) =>
      b.addEventListener("click", () => {
        const i = state?.type === "step" ? state.i + Number(b.dataset.dir) : 0;
        select({ type: "step", i: Math.max(0, Math.min(data.steps.length - 1, i)) });
      })
    );
  }

  let state = null;
  function light(nodes, edges) {
    const on = nodes.length > 0;
    svg.classList.toggle("is-focused", on);
    for (const [id, g] of Object.entries(nodeEls)) g.classList.toggle("is-lit", nodes.includes(id));
    for (const [id, { path }] of Object.entries(edgeEls)) path.classList.toggle("is-lit", edges.includes(id));
  }

  function select(next) {
    state = next;
    for (const [id, g] of Object.entries(nodeEls)) g.setAttribute("aria-pressed", String(next?.type === "node" && next.id === id));
    picker.querySelectorAll("[data-node]").forEach((b) => b.setAttribute("aria-pressed", String(next?.type === "node" && next.id === b.dataset.node)));
    steps?.querySelectorAll("[data-step]").forEach((b) => b.setAttribute("aria-pressed", String(next?.type === "step" && next.i === Number(b.dataset.step))));

    if (!next) {
      light([], []);
      panel.innerHTML = `<p class="ax__hint">Select a component in the diagram or in the list to see what it does and what it talks to.</p>`;
      if (steps) {
        steps.querySelector(".ax-steps__count").textContent = `${data.steps.length} steps`;
        steps.querySelector(".ax-steps__card").innerHTML = `<p class="ax__hint">Step through a purchase, from the Buy button to a paid or cancelled order. Each step lights the components involved.</p>`;
      }
      return;
    }

    if (next.type === "node") {
      const n = byId[next.id];
      const edges = data.edges.filter((e) => e.from === n.id || e.to === n.id);
      const near = new Set([n.id, ...edges.flatMap((e) => [e.from, e.to])]);
      light([...near], edges.map((e) => e.id));
      panel.innerHTML = `
        <h3 class="ax__name">${esc(n.label)}${n.sub ? ` <span>${esc(n.sub)}</span>` : ""}</h3>
        <p>${esc(n.role)}</p>
        <dl class="ax__facts">
          <dt>Technologies</dt><dd>${esc(n.tech)}</dd>
          ${n.db ? `<dt>Data</dt><dd>${esc(n.db)}</dd>` : ""}
          ${list("Incoming", n.in)}
          ${list("Outgoing", n.out)}
          ${list("Events", n.events)}
          ${list("Details", n.details)}
        </dl>
        ${n.link ? `<a class="ax__link" href="${esc(n.link)}" target="_blank" rel="noopener noreferrer"><i class="fa fa-github" aria-hidden="true"></i> Source on GitHub</a>` : ""}`;
      if (steps) {
        steps.querySelector(".ax-steps__count").textContent = `${data.steps.length} steps`;
        steps.querySelector(".ax-steps__card").innerHTML = `<p class="ax__hint">Pick a step to follow the purchase again.</p>`;
      }
    } else {
      const s = data.steps[next.i];
      light(s.nodes, s.edges);
      panel.innerHTML = `<p class="ax__hint">Step ${next.i + 1} lights ${esc(s.who)} in the diagram. Select a component for its details.</p>`;
      steps.querySelector(".ax-steps__count").textContent = `Step ${next.i + 1} of ${data.steps.length}`;
      steps.querySelector(".ax-steps__card").innerHTML = `
        <p class="ax-steps__who">${esc(s.who)}</p>
        <h4>${next.i + 1}. ${esc(s.title)}</h4>
        <p>${esc(s.what)}</p>
        <dl class="ax__facts">
          <dt>Data</dt><dd>${esc(s.data)}</dd>
          <dt>What can go wrong</dt><dd>${esc(s.wrong)}</dd>
          <dt>How it's handled</dt><dd>${esc(s.handled)}</dd>
        </dl>`;
    }
  }

  // Escape or a click on empty space clears the selection
  svg.addEventListener("click", (ev) => {
    if (!ev.target.closest(".ax-node")) select(null);
  });
  container.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && state) {
      ev.preventDefault(); // don't close the case-study panel along with it
      ev.stopPropagation();
      select(null);
    }
  });
  select(null);
}

export default function initArch(root = document) {
  for (const container of root.querySelectorAll("[data-arch]")) {
    const data = ARCHITECTURES[container.dataset.arch];
    if (data && !container.classList.contains("ax")) render(container, data);
  }
}
